import React from 'react';
import { useTranslation } from 'react-i18next';
import { RoleConflictView } from '../../../api/General/businessRoles';
import {
  Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter,
  DialogTitle, DialogDescription,
} from '../../../components/ui/Dialog';
import Button from '../../../components/ui/Button';

interface RoleConflictModalProps {
  open: boolean;
  conflicts: RoleConflictView[];
  onProceed: () => void;
  onCancel: () => void;
}

export default function RoleConflictModal({ open, conflicts, onProceed, onCancel }: RoleConflictModalProps) {
  const { t } = useTranslation();

  const hasErrors = conflicts.some(c => c.severity === 'error');

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onCancel(); }}>
      <DialogContent size="default">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-warning/10 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <DialogTitle>{t('businessRoles.conflictDetected')}</DialogTitle>
              <DialogDescription>{t('businessRoles.conflictDescription')}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-3 max-h-80 overflow-y-auto">
          {conflicts.map((conflict, index) => (
            <div
              key={index}
              className={`rounded-lg p-3 border ${
                conflict.severity === 'error'
                  ? 'bg-destructive/5 border-destructive/20'
                  : 'bg-warning/5 border-warning/20'
              }`}
            >
              <div className="flex items-start gap-2">
                {conflict.severity === 'error' ? (
                  <svg className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-warning mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                )}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${
                    conflict.severity === 'error' ? 'text-destructive' : 'text-warning'
                  }`}>
                    {conflict.message}
                  </p>
                  {conflict.existingRoleName && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('businessRoles.existingRole')}: {conflict.existingRoleName}
                    </p>
                  )}
                  {conflict.overlappingResponsibilities.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-muted-foreground mb-1">{t('businessRoles.overlappingResponsibilities')}:</p>
                      <div className="flex flex-wrap gap-1">
                        {conflict.overlappingResponsibilities.map((resp, i) => (
                          <span key={i} className="inline-flex px-1.5 py-0.5 bg-card/60 rounded text-xs text-foreground">{resp}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          {!hasErrors && (
            <Button variant="warning" onClick={onProceed}>
              {t('businessRoles.proceedAnyway')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
